import { Checkbox } from '@/components/ui/checkbox'
import { createTableStore } from '@/components/zthd/data-table'
import { cn } from '@/lib/utils'
import { LineChart } from 'lucide-react'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { getDrBaseline, getDrBaselineHistory, getCostBaseline, getProfitBaseline } from '@/lib/dr-utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useRoiTableStore from '@/store/roi-table'
import { ROI_REVENUE_SOURCES } from '@/constants/roi-revenue-source'
import { ROI_COST_SOURCES } from '@/constants/roi-cost-source'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'

const useTableStore = createTableStore({ columnSelection: {} }, 'roi')

const Td2 = ({ children, line2, line1Css, boldLine1 = false, className }) => {
  return (
    <div className={cn('flex flex-col text-right', className)}>
      <div className={cn('text-nowrap', boldLine1 && 'font-bold', line1Css)}>
        {children}
      </div>
      <div className="text-xs text-muted-foreground">{line2}</div>
    </div>
  )
}

// 生成对比信息的显示文本（根据显示选项控制显示内容）
const formatContrastInfo = (contrastValue, diff, diffPercent, formatter, showContrastValue, showDiff, showDiffPercent) => {
  if (contrastValue == null) {
    return '-'
  }

  const parts = []

  // 原始值（对比值）
  if (showContrastValue) {
    const formattedContrast = formatter(contrastValue)
    parts.push(formattedContrast)
  }

  // 差异值
  if (showDiff) {
    const diffValue = diff
    if (diffValue != null && !isNaN(diffValue)) {
      const diffFormatted = formatter(Math.abs(diffValue))
      const arrow = diffValue >= 0 ? '↑' : '↓'
      const colorClass = diffValue >= 0 ? 'text-green-600' : 'text-red-600'
      parts.push(`<span class="${colorClass}">${arrow}${diffFormatted}</span>`)
    }
  }

  // 差异百分比
  if (showDiffPercent) {
    if (diffPercent != null && !isNaN(diffPercent)) {
      const percentFormatted = diffPercent >= 0 ? `+${diffPercent.toFixed(1)}%` : `${diffPercent.toFixed(1)}%`
      const colorClass = diffPercent >= 0 ? 'text-green-600' : 'text-red-600'
      parts.push(`<span class="${colorClass}">${percentFormatted}</span>`)
    }
  }

  if (parts.length === 0) {
    return '-'
  }

  return parts.join(' ')
}

// 带对比信息的Td2组件
const Td2WithContrast = ({ 
  children, 
  contrastValue, 
  diff, 
  diffPercent, 
  line1Css, 
  boldLine1 = true, 
  className,
  formatter = (v) => String(v),
  originalLine2 = null,
}) => {
  // 使用 hook 订阅状态变化，确保组件能够响应状态更新
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const showContrastValue = useContrastModeStore((state) => state.showContrastValue)
  const showDiff = useContrastModeStore((state) => state.showDiff)
  const showDiffPercent = useContrastModeStore((state) => state.showDiffPercent)
  
  const line2 = contrastEnabled && contrastValue != null
    ? formatContrastInfo(contrastValue, diff, diffPercent, formatter, showContrastValue, showDiff, showDiffPercent)
    : (originalLine2 || '-')
  
  return (
    <div className={cn('flex flex-col text-right', className)}>
      <div className={cn('text-nowrap', boldLine1 && 'font-bold', line1Css)}>
        {children}
      </div>
      <div 
        className="text-xs text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: line2 }}
      />
    </div>
  )
}

export const selectRow = {
  id: 'select',
  header: ({ table }) => (
    <Checkbox
      checked={
        table.getIsAllPageRowsSelected() ||
        (table.getIsSomePageRowsSelected() && 'indeterminate')
      }
      onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      aria-label="Select all"
    />
  ),
  cell: ({ row }) => (
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(!!value)}
      aria-label="Select row"
    />
  ),
  enableSorting: false,
  enableHiding: false,
}

export const dnuColumn = {
  accessorKey: 'dnu',
  header: 'Dnu',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const number = parseFloat(data.dnu) || 0
    const formatted = formatNumber(number)
    const dau = parseFloat(data.dau) || 0
    const per = formatPercent(number / dau)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.dnu}
          diff={data.diff?.dnu}
          diffPercent={data.diffPercent?.dnu}
          formatter={formatNumber}
          originalLine2={per}
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2={per}>{formatted}</Td2>
  },
  enableSorting: false,
}

export const dauColumn = {
  accessorKey: 'dau',
  header: 'Dau',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const number = parseFloat(data.dau) || 0
    const formatted = formatNumber(number)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.dau}
          diff={data.diff?.dau}
          diffPercent={data.diffPercent?.dau}
          formatter={formatNumber}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}
export const originalColumn = {
  id: 'original',
  accessorKey: 'original',
  header: 'Original',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const number = parseFloat(data.original) || 0
    const formatted = formatNumber(number)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.original}
          diff={data.diff?.original}
          diffPercent={data.diffPercent?.original}
          formatter={formatNumber}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const installsColumn = {
  id: 'installs',
  accessorKey: 'installs',
  header: () => (
    <div className="text-center">
      Installs
      <div className="text-xs">(Buy)</div>
    </div>
  ),
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const number = parseFloat(data.installs) || 0
    const formatted = formatNumber(number)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.installs}
          diff={data.diff?.installs}
          diffPercent={data.diffPercent?.installs}
          formatter={formatNumber}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const revenueColumn = {
  id: 'revenue',
  accessorFn: (row) => row.revenue,
  header: () => <div className="text-right">Revenue</div>,
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const revenue = parseFloat(data.revenue) || 0
    const formatted = formatUSD(revenue, 0, 0)

    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.revenue}
          diff={data.diff?.revenue}
          diffPercent={data.diffPercent?.revenue}
          formatter={(v) => formatUSD(v, 0, 0)}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
  enableHiding: false,
}

const CostHeader = ({ table, costBaselines, country, pid }) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  let latestDate
  if (visibleDates.size > 0) {
    latestDate = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
  } else {
    const rows = table.getFilteredRowModel().rows
    latestDate = rows.reduce((maxDate, row) => {
      const currentDate = row.original.date
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const headerBaseline = getCostBaseline(costBaselines, country, pid, latestDate)
  const formatted = formatUSD(headerBaseline, 0, 0)

  return (
    <Header column={{ id: 'cost' }}>
      Cost
      {headerBaseline > 0 && (
        <span className="text-xs ml-1">({formatted})</span>
      )}
    </Header>
  )
}

export const costColumn = (costBaselines, country, pid) => ({
  id: 'cost',
  accessorKey: 'cost',
  header: ({ table }) => (
    <CostHeader
      table={table}
      costBaselines={costBaselines}
      country={country}
      pid={pid}
    />
  ),
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const cost = parseFloat(data.cost) || 0
    const date = data.date
    const cellBaseline = getCostBaseline(costBaselines, country, pid, date)
    const formatted = formatUSD(cost, 0, 0)
    const line1Css =
      cellBaseline > 0
        ? cost < cellBaseline
          ? 'text-destructive'
          : 'text-primary'
        : ''
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.cost}
          diff={data.diff?.cost}
          diffPercent={data.diffPercent?.cost}
          formatter={(v) => formatUSD(v, 0, 0)}
          line1Css={line1Css}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return (
      <Td2 line2="-" line1Css={line1Css}>
        {formatted}
      </Td2>
    )
  },
  enableHiding: false,
})
const Header = ({ column, children }) => {
  const columnSelection = useTableStore((state) => state.columnSelection)
  const setColumnSelection = useTableStore((state) => state.setColumnSelection)
  const isVisible = columnSelection[column.id]
  return (
    <div
      className={cn('flex flex-col font-bold items-center text-right')}
      onClick={() => {
        setColumnSelection((prevSelection) => ({
          ...prevSelection,
          [column.id]: !isVisible,
        }))
      }}
    >
      {children}
      {isVisible && (
        <>
          <LineChart className="w-4 h-4" />
        </>
      )}
    </div>
  )
}

const ProfitHeader = ({ table, profitBaselines, country, pid }) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  let latestDate
  if (visibleDates.size > 0) {
    latestDate = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
  } else {
    const rows = table.getFilteredRowModel().rows
    latestDate = rows.reduce((maxDate, row) => {
      const currentDate = row.original.date
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const headerBaseline = getProfitBaseline(profitBaselines, country, pid, latestDate)
  const formatted = formatUSD(headerBaseline, 0, 0)

  return (
    <Header column={{ id: 'profit' }}>
      Profit
      {headerBaseline !== 0 && (
        <span className="text-xs ml-1">({formatted})</span>
      )}
    </Header>
  )
}

export const profitColumn = (profitBaselines, country, pid) => ({
  id: 'profit',
  header: ({ table }) => (
    <ProfitHeader
      table={table}
      profitBaselines={profitBaselines}
      country={country}
      pid={pid}
    />
  ),
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const costs = data.costs?.total?.costs
    const revenues = data.revenues?.total
    const profit = revenues - costs
    const date = data.date
    const cellBaseline = getProfitBaseline(
      profitBaselines,
      country,
      pid,
      date,
    )

    const formatted = formatUSD(profit, 0, 0)
    const line1Css = cellBaseline !== 0 
      ? (profit < cellBaseline ? 'text-destructive' : 'text-primary')
      : ''
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      return (
        <Td2WithContrast
          contrastValue={data.contrast.profit}
          diff={data.diff?.profit}
          diffPercent={data.diffPercent?.profit}
          formatter={(v) => formatUSD(v, 0, 0)}
          line1Css={line1Css}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return (
      <Td2 
        line2="-"
        line1Css={line1Css}
      >
        {formatted}
      </Td2>
    )
  },
  enableHiding: false,
})

export const erColumn = (erBaseLine) => ({
  id: 'er',
  accessorKey: 'er',
  header: () => {
    const formatted = formatPercent(erBaseLine, 0, 0)
    return (
      <div className="flex items-center">
        ER
        <span className="text-xs">({formatted})</span>
      </div>
    )
  },
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <div className="text-right">-</div>
    const er = parseFloat(data.er) || 0
    const formatted = formatPercent(er / 100)
    const line1Css = er < erBaseLine * 100 ? 'text-destructive' : 'text-primary'
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    // 使用 hook 订阅状态变化
    const showContrastValue = useContrastModeStore((state) => state.showContrastValue)
    const showDiff = useContrastModeStore((state) => state.showDiff)
    const showDiffPercent = useContrastModeStore((state) => state.showDiffPercent)
    
    if (contrastEnabled && data.contrast && data.contrast.er != null) {
      const contrastEr = data.contrast.er
      const contrastFormatted = formatPercent(contrastEr / 100)
      const diffValue = data.diff?.er
      const diffPercent = data.diffPercent?.er
      
      const parts = []
      if (showContrastValue) {
        parts.push(contrastFormatted)
      }
      if (showDiff && diffValue != null) {
        const diffFormatted = formatPercent(Math.abs(diffValue) / 100)
        const arrow = diffValue >= 0 ? '↑' : '↓'
        const colorClass = diffValue >= 0 ? 'text-green-600' : 'text-red-600'
        parts.push(`<span class="${colorClass}">${arrow}${diffFormatted}</span>`)
      }
      if (showDiffPercent && diffPercent != null) {
        const percentFormatted = diffPercent >= 0 ? `+${diffPercent.toFixed(1)}%` : `${diffPercent.toFixed(1)}%`
        const colorClass = diffPercent >= 0 ? 'text-green-600' : 'text-red-600'
        parts.push(`<span class="${colorClass}">${percentFormatted}</span>`)
      }
      
      const contrastLine = parts.length > 0 ? parts.join(' ') : '-'
      
      return (
        <div className="flex flex-col text-right">
          <div className={cn('font-bold', line1Css)}>
            {formatted}
          </div>
          <div 
            className="text-xs text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: contrastLine }}
          />
        </div>
      )
    }
    return (
      <div className="flex flex-col text-right">
        <div className={cn('font-bold', line1Css)}>
          {formatted}
        </div>
        <div className="text-xs text-muted">-</div>
      </div>
    )
  },
  enableHiding: false,
})

const RoasHeader = ({ day, drBaselines, country, pid, packageName = null }) => {
  const {
    value: headerBaseline,
    isDefault,
    sourcePid,
    sourceMediaSource,
    isGroupFallback,
    isMediaFallback,
    isCountryFallback,
  } = getDrBaseline(drBaselines, day, country, pid, packageName)
  const formatted = formatPercent(headerBaseline, 0, 0)
  const history = getDrBaselineHistory(drBaselines, day, country, pid, packageName)

  const sourceHints = []
  if (isGroupFallback && sourcePid) {
    sourceHints.push(`Using group baseline ${sourcePid}`)
  }
  if (isMediaFallback && sourceMediaSource) {
    sourceHints.push(`Using channel fallback ${sourceMediaSource}`)
  }
  if (isCountryFallback) {
    sourceHints.push('Using overall country fallback')
  }

  const tooltipContent = history.length > 0 || sourceHints.length > 0 ? (
    <div className="space-y-1">
      {sourceHints.map((hint) => (
        <p key={hint} className="text-xs text-amber-600">
          {hint}
        </p>
      ))}
      {history.length > 0 && (
        <>
          <p className="font-medium">Baseline History:</p>
          {history.map((record, index) => (
            <p key={record.date} className="text-xs">
              {record.date}: {formatPercent(record.value, 0, 0)}
              {index === 0 && ' (current)'}
            </p>
          ))}
        </>
      )}
    </div>
  ) : (
    <p className="text-xs">No baseline configuration found</p>
  )

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="text-right cursor-help">
            ROAS-D{day}
            <span className={cn('text-xs ml-1', !isDefault && 'text-green-600 font-semibold')}>({formatted})</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export const day0RoasColumn = (drBaselines, country, pid, packageName = null) => ({
  id: 'day0Roas',
  accessorKey: 'day0Roas',
  header: ({ table }) => (
    <RoasHeader
      table={table}
      day={0}
      drBaselines={drBaselines}
      country={country}
      pid={pid}
      packageName={packageName}
    />
  ),
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <div className="text-gray-400 text-center">-</div>
    const roasData = data.day0Roas
    if (!roasData) {
      return <div className="text-gray-400 text-center">-</div>
    }

    const roas = roasData.avg_roas
    const { value: cellBaseline } = getDrBaseline(
      drBaselines,
      0,
      country,
      pid,
      packageName,
    )
    const line1Css = roas < cellBaseline ? 'text-destructive' : 'text-primary'
    const formatted = formatPercent(roas, 1)

    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    // 使用 hook 订阅状态变化
    const showContrastValue = useContrastModeStore((state) => state.showContrastValue)
    const showDiff = useContrastModeStore((state) => state.showDiff)
    const showDiffPercent = useContrastModeStore((state) => state.showDiffPercent)

    if (contrastEnabled && data.contrast && data.contrast.day0Roas) {
      const contrastRoas = data.contrast.day0Roas.avg_roas
      const contrastFormatted = formatPercent(contrastRoas, 1)
      const diffValue = data.diff?.day0Roas
      const diffPercent = data.diffPercent?.day0Roas

      const parts = []
      if (showContrastValue) {
        parts.push(contrastFormatted)
      }
      if (showDiff && diffValue != null) {
        const diffFormatted = formatPercent(Math.abs(diffValue), 1)
        const arrow = diffValue >= 0 ? '↑' : '↓'
        const colorClass = diffValue >= 0 ? 'text-green-600' : 'text-red-600'
        parts.push(`<span class="${colorClass}">${arrow}${diffFormatted}</span>`)
      }
      if (showDiffPercent && diffPercent != null) {
        const percentFormatted = diffPercent >= 0 ? `+${diffPercent.toFixed(1)}%` : `${diffPercent.toFixed(1)}%`
        const colorClass = diffPercent >= 0 ? 'text-green-600' : 'text-red-600'
        parts.push(`<span class="${colorClass}">${percentFormatted}</span>`)
      }

      const contrastLine = parts.length > 0 ? parts.join(' ') : '-'

      return (
        <div className="text-right">
          <div className={cn('font-bold', line1Css)}>
            {formatted}
          </div>
          <div
            className="text-xs text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: contrastLine }}
          />
        </div>
      )
    }

    return (
      <div className="text-right">
        <div className={cn('font-bold', line1Css)}>
          {formatted}
        </div>
        <div className="text-xs text-muted">-</div>
      </div>
    )
  },
})

export const day3RoasColumn = (drBaselines, country, pid, packageName = null) => ({
  id: 'day3Roas',
  accessorKey: 'day3Roas',
  header: ({ table }) => (
    <RoasHeader
      table={table}
      day={3}
      drBaselines={drBaselines}
      country={country}
      pid={pid}
      packageName={packageName}
    />
  ),
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <div className="text-gray-400 text-center">-</div>
    const roasData = data.day3Roas
    if (!roasData) {
      return <div className="text-gray-400 text-center">-</div>
    }

    const roas = roasData.avg_roas
    const { value: cellBaseline } = getDrBaseline(
      drBaselines,
      3,
      country,
      pid,
      packageName,
    )
    const line1Css = roas < cellBaseline ? 'text-destructive' : 'text-primary'
    const formatted = formatPercent(roas, 1)

    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    // 使用 hook 订阅状态变化
    const showContrastValue = useContrastModeStore((state) => state.showContrastValue)
    const showDiff = useContrastModeStore((state) => state.showDiff)
    const showDiffPercent = useContrastModeStore((state) => state.showDiffPercent)

    if (contrastEnabled && data.contrast && data.contrast.day3Roas) {
      const contrastRoas = data.contrast.day3Roas.avg_roas
      const contrastFormatted = formatPercent(contrastRoas, 1)
      const diffValue = data.diff?.day3Roas
      const diffPercent = data.diffPercent?.day3Roas

      const parts = []
      if (showContrastValue) {
        parts.push(contrastFormatted)
      }
      if (showDiff && diffValue != null) {
        const diffFormatted = formatPercent(Math.abs(diffValue), 1)
        const arrow = diffValue >= 0 ? '↑' : '↓'
        const colorClass = diffValue >= 0 ? 'text-green-600' : 'text-red-600'
        parts.push(`<span class="${colorClass}">${arrow}${diffFormatted}</span>`)
      }
      if (showDiffPercent && diffPercent != null) {
        const percentFormatted = diffPercent >= 0 ? `+${diffPercent.toFixed(1)}%` : `${diffPercent.toFixed(1)}%`
        const colorClass = diffPercent >= 0 ? 'text-green-600' : 'text-red-600'
        parts.push(`<span class="${colorClass}">${percentFormatted}</span>`)
      }

      const contrastLine = parts.length > 0 ? parts.join(' ') : '-'

      return (
        <div className="text-right">
          <div className={cn('font-bold', line1Css)}>
            {formatted}
          </div>
          <div
            className="text-xs text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: contrastLine }}
          />
        </div>
      )
    }

    return (
      <div className="text-right">
        <div className={cn('font-bold', line1Css)}>
          {formatted}
        </div>
        <div className="text-xs text-muted">-</div>
      </div>
    )
  },
})

export const cpnColumn = {
  id: 'cpn',
  accessorFn: (row) => {
    const cost = row?.cost || 0
    const dnu = row?.dnu || 0
    return dnu ? cost / dnu : 0
  },
  header: 'C/N',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const cost = parseFloat(data.cost) || 0
    const dnu = parseFloat(data.dnu) || 0
    const cpn = dnu ? cost / dnu : 0
    const formatted = formatUSD(cpn, 4, 4)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      const contrastCpn = data.contrast.dnu 
        ? (data.contrast.cost / data.contrast.dnu) 
        : 0
      const diff = cpn - contrastCpn
      const diffPercent = contrastCpn !== 0 ? (diff / contrastCpn) * 100 : null
      
      return (
        <Td2WithContrast
          contrastValue={contrastCpn}
          diff={diff}
          diffPercent={diffPercent}
          formatter={(v) => formatUSD(v, 4, 4)}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const cpiColumn = {
  id: 'cpi',
  accessorFn: (row) => {
    const cost = row?.cost || 0
    const installs = row?.installs || 0
    return installs ? cost / installs : 0
  },
  header: 'CPI',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const cost = parseFloat(data.cost) || 0
    const installs = parseFloat(data.installs) || 0
    const cpi = installs ? cost / installs : 0
    const formatted = formatUSD(cpi, 4, 4)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      const contrastCpi = data.contrast.installs 
        ? (data.contrast.cost / data.contrast.installs) 
        : 0
      const diff = cpi - contrastCpi
      const diffPercent = contrastCpi !== 0 ? (diff / contrastCpi) * 100 : null
      
      return (
        <Td2WithContrast
          contrastValue={contrastCpi}
          diff={diff}
          diffPercent={diffPercent}
          formatter={(v) => formatUSD(v, 4, 4)}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const rpiColumn = {
  id: 'rpi',
  accessorFn: (row) => {
    const revenues = row.revenues?.total
    const installs = row.costs?.total.installs
    const cpi = revenues / installs
    return cpi
  },
  header: 'RPI',
  cell: (row) => {
    const cpi = row.getValue()
    const formatted = formatUSD(cpi, 4, 4)
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const rpnColumn = {
  id: 'rpn',
  accessorFn: (row) => {
    const revenue = row?.revenue || 0
    const dnu = row?.dnu || 0
    return dnu ? revenue / dnu : 0
  },
  header: 'R/N',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const revenue = parseFloat(data.revenue) || 0
    const dnu = parseFloat(data.dnu) || 0
    const rpn = dnu ? revenue / dnu : 0
    const formatted = formatUSD(rpn, 4, 4)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      const contrastRpn = data.contrast.dnu 
        ? (data.contrast.revenue / data.contrast.dnu) 
        : 0
      const diff = rpn - contrastRpn
      const diffPercent = contrastRpn !== 0 ? (diff / contrastRpn) * 100 : null
      
      return (
        <Td2WithContrast
          contrastValue={contrastRpn}
          diff={diff}
          diffPercent={diffPercent}
          formatter={(v) => formatUSD(v, 4, 4)}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const rpaColumn = {
  id: 'rpa',
  accessorFn: (row) => {
    const revenue = row?.revenue || 0
    const dau = row?.dau || 0
    return dau ? revenue / dau : 0
  },
  header: 'R/A',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const revenue = parseFloat(data.revenue) || 0
    const dau = parseFloat(data.dau) || 0
    const rpa = dau ? revenue / dau : 0
    const formatted = formatUSD(rpa, 4, 4)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      const contrastRpa = data.contrast.dau 
        ? (data.contrast.revenue / data.contrast.dau) 
        : 0
      const diff = rpa - contrastRpa
      const diffPercent = contrastRpa !== 0 ? (diff / contrastRpa) * 100 : null
      
      return (
        <Td2WithContrast
          contrastValue={contrastRpa}
          diff={diff}
          diffPercent={diffPercent}
          formatter={(v) => formatUSD(v, 4, 4)}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const ltvColumn = {
  id: 'ltv',
  accessorFn: (row) => {
    const profit = row?.profit || 0
    const installs = row?.installs || 0
    return installs ? profit / installs : 0
  },
  header: 'LTV',
  cell: ({ row }) => {
    const data = row.original
    if (!data) return <Td2 line2="-">-</Td2>
    const profit = parseFloat(data.profit) || 0
    const installs = parseFloat(data.installs) || 0
    const ltv = installs ? profit / installs : 0
    const formatted = formatUSD(ltv, 4, 4)
    
    const contrastEnabled = useContrastModeStore((state) => state.enabled)
    if (contrastEnabled && data.contrast) {
      const contrastLtv = data.contrast.installs 
        ? (data.contrast.profit / data.contrast.installs) 
        : 0
      const diff = ltv - contrastLtv
      const diffPercent = contrastLtv !== 0 ? (diff / contrastLtv) * 100 : null
      
      return (
        <Td2WithContrast
          contrastValue={contrastLtv}
          diff={diff}
          diffPercent={diffPercent}
          formatter={(v) => formatUSD(v, 4, 4)}
          originalLine2="-"
        >
          {formatted}
        </Td2WithContrast>
      )
    }
    return <Td2 line2="-">{formatted}</Td2>
  },
}

export const costsDetailColumn = {
  id: 'costs',
  header: 'Costs',
  columns: [
    ...ROI_COST_SOURCES.map((source) => ({
      id: `costs-${source}`,
      header: source,
      columns: [
        {
          id: `costs-${source}-cost`,
          header: 'Cost',
          accessorFn: (row) => {
            const total = row?.costs?.total?.costs
            const curr = row?.costs?.[source]?.costs
            return { total, curr }
          },
          cell: (row) => {
            const meta = row.getValue()
            const { total, curr } = meta
            const formatted = formatUSD(curr, 0, 0)
            const per = formatPercent(curr / total)
            return <Td2 line2={per}>{formatted}</Td2>
          },
        },
        {
          id: `costs-${source}-install`,
          header: 'Install',
          accessorFn: (row) => {
            const total = row?.costs?.total?.installs
            const curr = row?.costs?.[source]?.installs
            return { total, curr }
          },
          cell: (row) => {
            const meta = row.getValue()
            const { total, curr } = meta
            const formatted = formatNumber(curr)
            const per = formatPercent(curr / total)
            return <Td2 line2={per}>{formatted}</Td2>
          },
        },
        {
          id: `costs-${source}-cpi`,
          header: 'CPI',
          accessorFn: (row) => {
            const costs = row?.costs?.[source]?.costs
            const installs = row?.costs?.[source]?.installs
            return costs / installs
          },
          cell: (row) => {
            const meta = row.getValue()
            const formatted = formatUSD(meta, 4, 4)
            return <Td2 line2="-">{formatted}</Td2>
          },
        },
      ],
    })),
  ],
}

export const revenueDetailColumn = {
  id: 'revenue-group',
  header: 'Revenue',
  columns: [
    ...ROI_REVENUE_SOURCES.map((source) => ({
      id: `revenues-${source}`,
      accessorKey: 'revenues',
      header: () => {
        if (source === 'applovin_admob') {
          return (
            <div className="text-right">
              <div className="capitalize">Applovin</div>
              <div>
                <small>(admob)</small>
              </div>
            </div>
          )
        } else if (source === 'google_play_subscription') {
          return (
            <div className="text-right">
              <div className="capitalize">Google Play</div>
              <div>
                <small>(subscription)</small>
              </div>
            </div>
          )
        } else if (source === 'moloco_admob') {
          return (
            <div className="text-right">
              <div className="capitalize">Moloco</div>
              <div>
                <small>(admob)</small>
              </div>
            </div>
          )
        } else if (source === 'waterfall_admob') {
          return (
            <div className="text-right">
              <div className="capitalize">admob</div>
              <div>
                <small>(waterfall)</small>
              </div>
            </div>
          )
        }
        return <div className="text-right">{source}</div>
      },
      cell: (row) => {
        const revenues = row.getValue('revenues')
        const revenue = parseFloat(revenues?.[source])
        const total = revenues?.total
        const formatted = formatUSD(revenue, 0, 0)
        const per = formatPercent(revenue / total)
        return <Td2 line2={per}>{formatted}</Td2>
      },
    })),
  ],
}
